import { Component, computed, input, output, Signal, signal, inject } from '@angular/core';
import { FieldPathDirective, FormComponent, FormNonFieldErrorsComponent, FormOptionsDirective } from '@bo/ng-forms';
import { CardFundsRequest } from '../../../../../../_interfaces/cards.interface';
import { Method } from '@bo/ng-api';
import { Observable } from 'rxjs';
import { OffcanvasService } from '../../../../../../_services/offcanvas-service/offcanvas.service';
import { CardsApiService } from '../../../../../../_services/cards-service/cards-api/cards-api.service';
import { CurrencyPipe, NgClass } from '@angular/common';
import { FieldComponent } from '../../../../../../shared/components/forms/form-field/form-field.component';
import { ReactiveFormsModule } from '@angular/forms';
import { SubmitComponent } from '../../../../../../shared/components/forms/submit/submit.component';
import { maskConfig } from '../../../../../../shared/configs/configs';
import { LoaderComponent } from '../../../../../../shared/components/loader/loader.component';
import { CardTopupsService } from '../../../../../../_services/topup-service/card-topups.service';
import { EmptyIndicatorPipe } from '../../../../../../shared/pipes/empty-indicator.pipe';

@Component({
  selector: 'app-immediate-top-up-load-form',
  imports: [
    CurrencyPipe,
    FieldComponent,
    FieldPathDirective,
    FormNonFieldErrorsComponent,
    ReactiveFormsModule,
    SubmitComponent,
    FormOptionsDirective,
    NgClass,
    LoaderComponent,
    EmptyIndicatorPipe,
  ],
  templateUrl: './immediate-top-up-load-form.component.html',
})
export class ImmediateTopUpLoadFormComponent extends FormComponent<CardFundsRequest> {
  protected offcanvasService = inject(OffcanvasService);
  protected cardsApiService = inject(CardsApiService);
  protected cardTopupsService = inject(CardTopupsService);

  selectedCardId = input.required<number>();
  cardBalance = input.required<string>();
  cardBalanceError = input<boolean>();
  isLoadFunds = input<boolean>(true);
  submitSuccess = output<boolean>();

  protected hasCardLimitError = signal<boolean>(false);
  protected hasReachedCardLimit = signal<boolean>(false);
  protected amountExceedsLimit = signal<boolean>(false);
  protected cardLimitAmount = signal<number>(0);
  protected cardBalanceLimit: Signal<number | undefined> = computed(() => this.cardTopupsService.cardLimits()?.balance);

  protected readonly fields: string[] = ['amount'];

  protected setLimitError(amount?: number) {
    if (this.isLoadFunds()) {
      this.cardLimitAmount.set(Number(this.cardBalanceLimit()) - Number(this.cardBalance()));
    } else {
      this.cardLimitAmount.set(Number(this.cardBalance()));
    }
    if (this.cardBalanceLimit() && amount) {
      this.amountExceedsLimit.set(amount > this.cardLimitAmount());
    }
    this.hasReachedCardLimit.set(this.cardLimitAmount() === 0);
    this.hasCardLimitError.set(this.amountExceedsLimit() || this.hasReachedCardLimit());
  }

  override createForm(): void {
    const options$ = this.cardsApiService.getOptions(this.cardsApiService.URL_CARD_FUNDS_LOAD + '/', Method.POST);
    this.subscriptions.add(
      this.buildFromOptions(options$).subscribe({
        next: () => {
          if (this.cardBalanceError()) {
            this.form.get('amount')?.disable();
          } else {
            this.handleTopUpAmountChanges();
            this.setLimitError();
          }
        },
      }),
    );
  }

  protected handleTopUpAmountChanges(): void {
    this.form.get('amount')?.valueChanges.subscribe((amount) => {
      this.setLimitError(amount);
      if (amount === 0) this.form.get('amount')?.setValue(undefined, { emitEvent: false });
    });
  }

  override submitData(formData: CardFundsRequest): Observable<CardFundsRequest> {
    if (this.selectedCardId()) formData.card = this.selectedCardId();
    return this.cardsApiService.loadCardFunds(formData);
  }

  handleSubmitSuccess(): void {
    this.submitSuccess.emit(true);
  }

  protected readonly maskConfig = maskConfig;
  protected readonly Number = Number;
}
